/**
 * useGlobalMessageSocket — listener global de notificação de novas mensagens.
 *
 * Escuta `CONVERSATION_MESSAGE_NOTIFY` (evento user-specific dedicado). O
 * backend emite apenas nos rooms `user:{userId}` dos destinatários elegíveis
 * (não-mutados + com acesso à conversa). Ou seja: **presença do evento =
 * permissão pra exibir toast e invalidar caches**.
 *
 * ⚠️ Som NÃO mora mais aqui. Desde 2026-04-30 o canal `sound` é first-class:
 * passa por `determineChannels` no backend e tem seu próprio dispatcher
 * (`SoundChannel`) que emite `notification:sound` em `user:{userId}` quando
 * a preferência permite. O frontend escuta esse evento dedicado em
 * `useNotificationSoundSocket`. Fechamento de loop "user desligou push mas
 * som continuava tocando".
 *
 * NOTA: este hook NÃO escuta `CONVERSATION_MESSAGE` — esse continua em
 * `useChatSocket` (usado pela sidebar e chat pra atualizar UI state).
 * Separação de concerns: UI state (broadcast) vs notification (user-specific).
 *
 * Deve ser montado exatamente 1x no root da app (via `GlobalSocketListeners`).
 */

import { useQueryClient } from '@tanstack/react-query';
import { SOCKET_EVENTS as Events } from '@troia-v3/types';
import type { ConversationMessageNotifyEvent } from '@troia-v3/types';
import { useEffect } from 'react';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';
import { useSocket } from '@/contexts/SocketContext';

export const useGlobalMessageSocket = (): void => {
  const queryClient = useQueryClient();
  const { socket } = useSocket();
  const { state: authState } = useAuth();

  useEffect(() => {
    if (!socket || !authState.user) return;

    const handler = (payload: ConversationMessageNotifyEvent): void => {
      toast.success('Nova mensagem na conversa!');

      // Invalida caches (útil quando user não tava na conv) — sidebar atualiza
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['conversation', payload.conversationId] });
    };

    socket.on(Events.CONVERSATION_MESSAGE_NOTIFY, handler);
    return () => {
      socket.off(Events.CONVERSATION_MESSAGE_NOTIFY, handler);
    };
  }, [socket, authState.user, queryClient]);
};
